import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Ban, CalendarDays, Copy, ExternalLink, PhoneCall, PhoneForwarded, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AdvisorSettingsCard } from '@/components/advisor/AdvisorSettingsCard';
import { MetaLeadAdsCard } from '@/components/advisor/MetaLeadAdsCard';
import { AddCallbackDialog, BlockTimeDialog } from '@/components/advisor/DiaryDialogs';

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}/api${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'Request failed');
  return d;
};
const TZ = 'Europe/London';
const when = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: TZ });
const TONE = { confirmed: 'bg-emerald-50 text-emerald-800', attended: 'bg-blue-50 text-blue-800', no_show: 'bg-rose-50 text-rose-800', cancelled: 'bg-gray-100 text-gray-500' };
const SOURCE = { meta_lead: 'Meta ad', booking_page: 'Booking page', staff: 'Added by advisor' };

function BlockRow({ b, onRemove }) {
  return (
    <div className="p-3 flex flex-wrap items-center gap-3 bg-rose-50/40" data-testid={`adv-block-${b.id}`}>
      <div className="w-32 shrink-0"><div className="font-bold text-sm">{when(b.start)}</div><div className="text-[11px] text-gray-500">until {when(b.end)}</div></div>
      <div className="flex-1 min-w-[180px] text-sm font-semibold inline-flex items-center gap-1.5 text-rose-800"><Ban className="w-4 h-4" /> {b.reason}</div>
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onRemove(b)} data-testid={`adv-block-remove-${b.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button>
    </div>
  );
}

function BookingRow({ b, onStatus }) {
  return (
    <div className="p-3 flex flex-wrap items-center gap-3" data-testid={`adv-booking-${b.id}`}>
      <div className="w-32 shrink-0"><div className="font-bold text-sm">{when(b.start)}</div><div className="text-[11px] text-gray-500">{SOURCE[b.source] || b.source}</div></div>
      <div className="flex-1 min-w-[180px]"><div className="font-semibold text-sm">{b.name}</div><div className="text-xs text-gray-600"><a href={`tel:${b.phone}`} className="underline">{b.phone}</a> · {b.email}</div>{b.notes && <div className="text-xs text-gray-500 mt-0.5 italic">“{b.notes}”</div>}</div>
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TONE[b.status]}`} data-testid={`adv-status-${b.id}`}>{b.status.replace('_', ' ')}</span>
      {b.status === 'confirmed' && (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onStatus(b, 'attended')} data-testid={`adv-attended-${b.id}`}>Attended</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700" onClick={() => onStatus(b, 'no_show')} data-testid={`adv-noshow-${b.id}`}>No show</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onStatus(b, 'cancelled')} data-testid={`adv-cancel-${b.id}`}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

export default function AdvisorDiary() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [scope, setScope] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const canEdit = user?.role === 'admin';
  const load = useCallback(() => Promise.all([api(`/advisor/bookings?scope=${scope}`), api('/advisor/blocks')]).then(([d, bl]) => { setRows(d.bookings); setBlocks(bl.blocks); }).catch((e) => toast.error(e.message)), [scope]);
  const removeBlock = async (b) => { if (!window.confirm(`Remove the block "${b.reason}"?`)) return; try { await api(`/advisor/blocks/${b.id}`, { method: 'DELETE' }); load(); } catch (e) { toast.error(e.message); } };
  const items = useMemo(() => {
    const list = rows.map((b) => ({ kind: 'booking', at: b.start, b }));
    if (scope !== 'past') blocks.forEach((b) => list.push({ kind: 'block', at: b.start, b }));
    return list.sort((x, y) => (scope === 'past' ? y.at.localeCompare(x.at) : x.at.localeCompare(y.at)));
  }, [rows, blocks, scope]);
  useEffect(() => { if (user) { api('/advisor/settings').then(setSettings).catch((e) => toast.error(e.message)); } }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) load(); }, [user?.user_id, load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const g = params.get('google');
    if (g === 'connected') toast.success('Google Calendar connected');
    if (g === 'failed') toast.error('Google Calendar connection failed — try again');
  }, [params]);
  const setStatus = async (b, status) => {
    if (status === 'cancelled' && !window.confirm(`Cancel ${b.name}'s chat? They'll be told by email/text.`)) return;
    try { await api(`/advisor/bookings/${b.id}/status`, { method: 'POST', body: JSON.stringify({ status }) }); load(); } catch (e) { toast.error(e.message); }
  };
  const url = settings?.booking_url;
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back" data-testid="adv-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1"><h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2"><PhoneCall className="w-6 h-6" style={{ color: '#D4A24B' }} /> Course Advisor Diary</h1><p className="text-white/70 text-sm">Chat bookings from your ads and website · callbacks · blocked-out time</p></div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {url && (
          <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="adv-link">
            <CalendarDays className="w-4 h-4 text-[#1f4030]" /><span className="text-sm font-semibold">Booking link for your ads:</span>
            <code className="text-xs bg-gray-100 rounded px-2 py-1" data-testid="adv-link-url">{url}</code>
            <Button size="sm" variant="outline" className="h-7" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success('Link copied'))} data-testid="adv-link-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <a href={url} target="_blank" rel="noreferrer" className="text-xs underline inline-flex items-center gap-1 text-gray-600" data-testid="adv-link-open">Open <ExternalLink className="w-3 h-3" /></a>
          </Card>
        )}
        <Card data-testid="adv-bookings">
          <div className="px-3 py-2 border-b flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-sm flex-1">Diary</h2>
            <Button size="sm" className="h-7 text-xs bg-[#1f4030] text-white" onClick={() => setDialog('add')} data-testid="adv-add-btn"><PhoneForwarded className="w-3.5 h-3.5 mr-1" /> Book a callback</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-200" onClick={() => setDialog('block')} data-testid="adv-block-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Block out time</Button>
            <span className="w-px h-5 bg-gray-200 mx-1 hidden sm:block" />
            {['upcoming', 'past', 'all'].map((s) => <Button key={s} size="sm" variant={scope === s ? 'default' : 'outline'} className={`h-7 text-xs ${scope === s ? 'bg-[#1f4030] text-white' : ''}`} onClick={() => setScope(s)} data-testid={`adv-scope-${s}`}>{s}</Button>)}
          </div>
          <div className="divide-y">{items.length ? items.map((it) => (it.kind === 'block' ? <BlockRow key={`bl-${it.b.id}`} b={it.b} onRemove={removeBlock} /> : <BookingRow key={it.b.id} b={it.b} onStatus={setStatus} />)) : <p className="p-6 text-sm text-gray-500 text-center" data-testid="adv-empty">No {scope} bookings.</p>}</div>
        </Card>
        <AddCallbackDialog open={dialog === 'add'} onClose={() => setDialog(null)} onDone={load} slotMinutes={settings?.slot_minutes || 15} />
        <BlockTimeDialog open={dialog === 'block'} onClose={() => setDialog(null)} onDone={load} />
        <div className="grid lg:grid-cols-2 gap-4">
          <AdvisorSettingsCard settings={settings} onSaved={setSettings} canEdit={canEdit} />
          <MetaLeadAdsCard canEdit={canEdit} />
        </div>
      </div>
    </div>
  );
}
